import type { ReactNode } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useSelector } from 'react-redux';

import { generatePdfFilename } from '../../utils/file';
import { makeAPIRequestReactQueryWithHeaders } from '../api';
import { endpoints } from '../endpoints';

interface GenerateBenefitsPdfRequest {
  unifiedAppointmentId: string;
  payerId?: number;
  patientName?: string;
}

interface GenerateBenefitsPdfResponse {
  success: boolean;
  message: ReactNode;
}

/**
 * React Query mutation hook for generating and downloading Benefits PDFs
 *
 * @param options - Optional mutation options
 * @returns React Query mutation object
 */
export const useGenerateBenefitsPdf = (
  options?: Parameters<
    typeof useMutation<
      GenerateBenefitsPdfResponse,
      AxiosError,
      GenerateBenefitsPdfRequest
    >
  >[0],
) => {
  const { t } = useLingui();
  const user = useSelector(
    (state: {
      login?: {
        user?: { primaryPracticeId?: string; primaryOrganizationId?: string };
      };
    }) => state?.login?.user,
  );
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;

  return useMutation({
    mutationFn: async (
      request: GenerateBenefitsPdfRequest,
    ): Promise<GenerateBenefitsPdfResponse> => {
      try {
        const requestBody: { unifiedAppointmentId: string; payerId?: number } =
          {
            unifiedAppointmentId: request.unifiedAppointmentId,
          };
        if (request.payerId !== undefined) {
          requestBody.payerId = request.payerId;
        }

        const response = await makeAPIRequestReactQueryWithHeaders<Blob>(
          endpoints.benefitsPdfGenerate,
          undefined,
          {
            Practice: currentPracticeId,
            Org: currentOrganizationId,
          },
          'POST',
          {
            data: requestBody,
            responseType: 'blob',
          },
        );

        // Create a download link
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;

        // Generate file name based on patient name if available
        link.download = generatePdfFilename(
          'benefits-summary',
          request.unifiedAppointmentId,
          request.patientName,
        );

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL object
        window.URL.revokeObjectURL(url);

        return { success: true, message: t`PDF downloaded successfully` };
      } catch (error) {
        console.error('Error generating Benefits PDF:', error);
        throw new Error('Failed to generate Benefits PDF');
      }
    },
    ...options,
  });
};
